import { getAccessToken } from '../../shared/utils/authStorage';

export interface HttpRequestOptions {
  authenticated?: boolean;
  cache?: RequestCache;
  includeContentType?: boolean;
  headers?: Record<string, string>;
  body?: any;
}

export interface HttpResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
}

export class HttpError<T = unknown> extends Error {
  data: T;
  headers: Headers;
  status: number;
  statusText: string;
  url: string;

  constructor(response: Response, data: T, url: string) {
    super(`HTTP Error: ${response.status} ${response.statusText}`);
    this.name = 'HttpError';
    this.data = data;
    this.headers = response.headers;
    this.status = response.status;
    this.statusText = response.statusText;
    this.url = url;
  }
}

export class HttpRepository {
  private baseUrl: string;

  constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl;
  }

  private async parseResponseData<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type') ?? '';
    const text = await response.text();
    const trimmedText = text.trim();

    if (!trimmedText) {
      return null as T;
    }

    if (contentType.includes('application/json') || trimmedText.startsWith('{') || trimmedText.startsWith('[')) {
      try {
        return JSON.parse(trimmedText) as T;
      } catch {
        return text as unknown as T;
      }
    }

    return text as unknown as T;
  }

  private buildUrl(url: string): string {
    if (/^https?:\/\//i.test(url)) return url;
    if (!this.baseUrl) return url;

    return `${this.baseUrl.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
  }

  private getDefaultHeaders(options: HttpRequestOptions = {}): Record<string, string> {
    const headers: Record<string, string> = {};

    if (options.includeContentType !== false) {
      headers['Content-Type'] = 'application/json';
    }

    const accessToken = getAccessToken();
    if (options.authenticated !== false && accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    return headers;
  }

  /**
   * Faz uma requisição HTTP genérica
   */
  private async request<T>(
    url: string,
    method: string,
    options: HttpRequestOptions = {}
  ): Promise<HttpResponse<T>> {
    const fullUrl = this.buildUrl(url);
    const headers = { ...this.getDefaultHeaders(options), ...options.headers };

    const config: RequestInit = {
      cache: options.cache,
      method,
      headers,
    };

    if (options.body && method !== 'GET') {
      config.body = typeof options.body === 'string' 
        ? options.body 
        : JSON.stringify(options.body);
    }

    try {
      const response = await fetch(fullUrl, config);
      const data = await this.parseResponseData<T>(response);

      if (!response.ok) {
        throw new HttpError(response, data, fullUrl);
      }

      return {
        data,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Faz uma requisição GET
   */
  async get<T>(url: string, options: HttpRequestOptions = {}): Promise<HttpResponse<T>> {
    return this.request<T>(url, 'GET', options);
  }

  /**
   * Faz uma requisição POST
   */
  async post<T>(url: string, body?: any, options: HttpRequestOptions = {}): Promise<HttpResponse<T>> {
    return this.request<T>(url, 'POST', { ...options, body });
  }

  /**
   * Faz uma requisição PUT
   */
  async put<T>(url: string, body?: any, options: HttpRequestOptions = {}): Promise<HttpResponse<T>> {
    return this.request<T>(url, 'PUT', { ...options, body });
  }

  /**
   * Faz uma requisição DELETE
   */
  async delete<T>(url: string, options: HttpRequestOptions = {}): Promise<HttpResponse<T>> {
    return this.request<T>(url, 'DELETE', options);
  }

  /**
   * Faz upload de arquivo
   */
  async uploadFile<T>(url: string, file: File): Promise<HttpResponse<T>> {
    const fullUrl = this.buildUrl(url);
    const formData = new FormData();
    formData.append('file', file);

    const headers = this.getDefaultHeaders({ includeContentType: false });

    // Remove Content-Type para permitir que o browser defina o boundary
    const config: RequestInit = {
      method: 'POST',
      headers,
      body: formData,
    };

    try {
      const response = await fetch(fullUrl, config);
      const data = await this.parseResponseData<T>(response);

      if (!response.ok) {
        throw new HttpError(response, data, fullUrl);
      }

      return {
        data,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Faz upload de FormData (para múltiplos arquivos)
   */
  async uploadFormData<T>(url: string, formData: FormData): Promise<HttpResponse<T>> {
    const fullUrl = this.buildUrl(url);
    const headers = this.getDefaultHeaders({ includeContentType: false });

    // Remove Content-Type para permitir que o browser defina o boundary
    const config: RequestInit = {
      method: 'POST',
      headers,
      body: formData,
    };

    try {
      const response = await fetch(fullUrl, config);
      const data = await this.parseResponseData<T>(response);

      if (!response.ok) {
        throw new HttpError(response, data, fullUrl);
      }

      return {
        data,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Faz download de arquivo binário
   */
  async downloadFile(url: string, options: HttpRequestOptions = {}): Promise<HttpResponse<ArrayBuffer>> {
    const fullUrl = this.buildUrl(url);
    const headers = {
      ...this.getDefaultHeaders({ ...options, includeContentType: false }),
      ...options.headers,
    };

    const config: RequestInit = {
      method: 'GET',
      headers,
    };

    try {
      const response = await fetch(fullUrl, config);
      
      if (!response.ok) {
        throw new HttpError(response, null, fullUrl);
      }

      const data = await response.arrayBuffer();

      return {
        data,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      };
    } catch (error) {
      throw error;
    }
  }
}

// Instância padrão para uso em toda a aplicação
export const httpRepository = new HttpRepository(import.meta.env.VITE_API_URL_ALT || '');
